// Helpers for the timesheet screens: typing and showing time, and week dates.
// Time is kept in whole minutes. Dates are plain "YYYY-MM-DD" calendar days.

export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected'

export const STATUS_LABEL: Record<TimesheetStatus, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Sent back',
}

export const canEditStatus = (status: TimesheetStatus) => status === 'draft' || status === 'rejected'

/**
 * What a person typed as a duration, in minutes, or null if it is not a time.
 * Accepts 1.5, 1:30, 90m, 7h, 7h 30m, 7.5h. An empty box is null.
 */
export function parseDuration(text: string | null | undefined): number | null {
  const t = String(text ?? '').trim().toLowerCase()
  if (t === '') return null

  const clock = /^(\d{1,2}):([0-5]\d)$/.exec(t)
  if (clock) return Number(clock[1]) * 60 + Number(clock[2])

  const hm = /^(?:(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?)?\s*(?:(\d+)\s*m(?:in(?:ute)?s?)?)?$/.exec(t)
  if (hm && (hm[1] !== undefined || hm[2] !== undefined)) {
    return Math.round(Number(hm[1] ?? 0) * 60 + Number(hm[2] ?? 0))
  }

  if (/^\d+(\.\d+)?$/.test(t)) return Math.round(Number(t) * 60)
  return null
}

/** Minutes as "7h 30m" (or "45m", "8h"); 0 is "0h". */
export function formatDuration(minutes: number | null | undefined): string {
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0 && m === 0) return '0h'
  if (h === 0) return `${m}m`
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

/** Minutes as the text a person would type back into the box: "1:30". */
export function durationInputValue(minutes: number | null | undefined): string {
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  if (total === 0) return ''
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

const pad = (n: number) => String(n).padStart(2, '0')
const toUtc = (date: string) => {
  const [y, m, d] = date.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}
const fmt = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`

/** The "YYYY-MM-DD" day for either form: a plain date or an ISO timestamp. Undefined stays undefined. */
const dayString = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined
  return value.slice(0, 10)
}

export function addDays(date: string, days: number): string {
  const d = toUtc(date)
  d.setUTCDate(d.getUTCDate() + days)
  return fmt(d)
}

/** The Monday of the week a date is in. */
export function weekStartOf(date: string): string {
  return addDays(date, -((toUtc(date).getUTCDay() + 6) % 7))
}

export const weekDates = (weekStart: string): string[] => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** "12 Oct". */
export const shortDay = (date: string) => `${toUtc(date).getUTCDate()} ${MONTHS[toUtc(date).getUTCMonth()]}`

/** "Mon". */
export const weekdayName = (date: string) => WEEKDAYS[toUtc(date).getUTCDay()]

/** "12 Oct - 18 Oct 2026". Falls back to an ISO weekStart when the plain weekStartDate is missing. */
export function weekLabel(weekStartDate: string | null | undefined, fallback?: string | null): string {
  const start = dayString(weekStartDate) ?? dayString(fallback)
  if (!start) return '—'
  const end = addDays(start, 6)
  return `${shortDay(start)} - ${shortDay(end)} ${toUtc(end).getUTCFullYear()}`
}

/** A whole number of weeks forward (or back, when negative). */
export const shiftWeek = (weekStart: string, weeks: number) => addDays(weekStart, weeks * 7)
